import React, { FunctionComponent } from 'react';
import { Block, BlockTypes } from '../../types/api/Block';
import { FetchedContainer } from '../FetchContainer';
import { BlockContextProvider } from './BlockContext';
import { RefreshBlockContextProvider, RefreshableBlock } from './RefreshBlockContext';
import { ErrorBoundary } from '../components/ErrorBoundary/ErrorBoundary';

export function asFetchableBlock<Embedded, BlockType extends BlockTypes>(
  Component: FunctionComponent<Embedded>,
  // used for type inference
  _?: BlockType,
  ...refreshableBlocks: RefreshableBlock[]
): FunctionComponent<Block<Embedded, BlockType>>;
export function asFetchableBlock<Embedded, BlockType extends BlockTypes>(
  Component: FunctionComponent<Embedded>,
  // used for type inference
  _?: BlockType,
  fallbackResponseWhenResponseIsNullOrUndefined?: Embedded
): FunctionComponent<Block<Embedded, BlockType>>;
export function asFetchableBlock<Embedded, BlockType extends BlockTypes>(
  Component: FunctionComponent<Embedded>,
  // used for type inference
  _?: BlockType,
  fallbackResponseWhenResponseIsNullOrUndefined?: Embedded,
  ...refreshableBlocks: RefreshableBlock[]
): FunctionComponent<Block<Embedded, BlockType>> {
  const BlockContainer = FetchedContainer<Embedded, Block<Embedded, BlockType>>;

  // changes in key create a new Component Instance.
  // new fetchUrl means previously fetched data is invalid.
  //  -> This happens when switching a page from edit to live mode
  //  -> Blocks must not display data from old url -> editMode=true or false in fetchUrl yields different results
  const FetchableBlock = function (props: Block<Embedded, BlockType>) {
    return (
      <ErrorBoundary key={`error-boundary-${props.uuid}`}>
        <BlockContainer
          {...props}
          key={props.fetchUrl}
          fallbackResponseWhenResponseIsNullOrUndefined={
            typeof fallbackResponseWhenResponseIsNullOrUndefined === 'string'
              ? undefined
              : fallbackResponseWhenResponseIsNullOrUndefined
          }
        >
          {({ embedded, refresh, refreshCount, refreshProps }) => (
            <BlockContextProvider
              uuid={props.uuid}
              elementType={props.elementType}
              fetchUrl={props.fetchUrl}
              blockLayout={props.blockLayout}
            >
              <RefreshBlockContextProvider
                refresh={refresh}
                refreshCount={refreshCount}
                refreshProps={refreshProps}
                uuid={props.uuid}
                resourceTypes={
                  typeof fallbackResponseWhenResponseIsNullOrUndefined === 'string'
                    ? [fallbackResponseWhenResponseIsNullOrUndefined as RefreshableBlock, ...refreshableBlocks]
                    : refreshableBlocks
                }
              >
                <Component {...embedded} elementType={props.elementType} />
              </RefreshBlockContextProvider>
            </BlockContextProvider>
          )}
        </BlockContainer>
      </ErrorBoundary>
    );
  };

  FetchableBlock.displayName = `AsFetchable${Component.displayName || Component.name}Block`;
  return FetchableBlock;
}
